import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate, Routes, Route } from "react-router-dom";
import { Link } from "react-router-dom";
import Theory from "./theory";
import TaskModal from "./TaskModal";
import fetchProtectedData from "../auth/ProtectDataHeader";
import withTokenRefresh from "../auth/RefreshToken";
import ReactMarkdown from 'react-markdown'
import SyntaxHighlighter from "react-syntax-highlighter";
import { irBlack } from "react-syntax-highlighter/dist/esm/styles/hljs";
import ChatModal from "./ChatModal";
import "./TheoryPage.css";
import RecordRTC, { StereoAudioRecorder } from "recordrtc";
import mermaid from 'mermaid';
import remarkGfm from 'remark-gfm';

import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";

import { ReactComponent as MicIcon } from "../../icons/voice.svg";
import { ReactComponent as ChatIcon } from "../../icons/text.svg";
import { ReactComponent as BookIcon } from "../../icons/book.svg";
import { ReactComponent as HomeIcon } from "../../icons/home.svg";

import {ReactComponent as PlayIcon} from "../../icons/play_icon.svg";
import {ReactComponent as PauseIcon} from "../../icons/pause_icon.svg";
import {ReactComponent as LeftIcon} from "../../icons/left_icon.svg";
import {ReactComponent as RightIcon} from "../../icons/right_icon.svg";

mermaid.initialize({
  startOnLoad: false,
  theme: 'default',
});

function Mermaid({ chart }) {
  const ref = useRef(null);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const id = 'mmd-' + Math.random().toString(36).slice(2);
    
    try {
      mermaid.render(id, chart)
        .then((result) => {
          if (ref.current && result.svg) {
            ref.current.innerHTML = result.svg;
            setHasError(false);
          }
        })
        .catch((error) => {
          console.warn('Mermaid rendering error:', error);
          setHasError(true);
        });
    } catch (error) {
      console.warn('Mermaid parsing error:', error);
      setHasError(true);
    }
  }, [chart]);

  // If there's an error, don't render anything
  if (hasError) {
    return null;
  }

  return <div ref={ref} className="mermaid" />;
}


async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {}
}

export default function TheoryPage() {
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [maxVisibleIndex, setMaxVisibleIndex] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const delayTimer = useRef(null);
  const navigate = useNavigate();

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const selectedTopic = params.get("topic") || "Not found";
  const selectedCourse = params.get("course_name") || "Not found";
  const title = params.get("topic") || "Not found";

  const summaryRef = useRef(null);
  const partRefs = useRef([]);
  const [showModal, setShowModal] = useState(false);
  const pageLoc = useLocation();
  const [theoryData, setTheoryData] = useState(null);

  const [user_id, setUserId] = useState(null);

  const [useVoice, setUseVoice] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [showRecorder, setShowRecorder] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [conversation, setConversation] = useState([]);


  const [voiceState, setVoiceState] = useState("idle"); // "idle" | "recording" | "loading" | "playing"

  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const responseAudioRef = useRef(new Audio());

  async function ensureMicAccess() {
    // some browsers support the Permissions API
    if (navigator.permissions && navigator.permissions.query) {
      try {
        const status = await navigator.permissions.query({ name: "microphone" });
        if (status.state === "denied") {
          alert(
            "Доступ к микрофону заблокирован в настройках браузера.\n" +
            "Разрешите использование микрофона и попробуйте снова."
          );
          return false;
        }
        // if status is "prompt" or "granted", we fall through to actually requesting
      } catch (e) {
        // Permissions API might not be implemented — ignore
      }
    }
    return true;
  }  

  const { transcript, listening, resetTranscript } = useSpeechRecognition({
    language: "ru-RU",
  });

  const startRecording = async () => {
    const ok = await ensureMicAccess();
    if (!ok) return;
    
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      console.error("Не удалось получить аудио-поток:", err);
      alert(
        "Нужен доступ к микрофону, чтобы задавать вопросы голосом.\n" +
        "Пожалуйста, разрешите доступ или попробуйте снова."
      );
      return;
    }
  
    streamRef.current = stream;

    const recorder = new RecordRTC(stream, {
      type: "audio",
      mimeType: "audio/wav",
      recorderType: StereoAudioRecorder,
      desiredSampRate: 16000,
      numberOfAudioChannels: 1,
    });
    recorder.startRecording();
    recorderRef.current = recorder;
    setIsRecording(true);
    resetTranscript();
    setVoiceState("recording");

    SpeechRecognition.startListening({ continuous: true, language: "ru-RU" });
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();  
    analyser.fftSize = 2048;
    source.connect(analyser);
    const data = new Uint8Array(analyser.fftSize);

    let silenceStart = null;
    const SILENCE_THRESHOLD = 0.02; // rms below ~0.02 is "silent"
    const SILENCE_DELAY = 2000; // ms of continuous silence → stop

    function detectSilence() {
      analyser.getByteTimeDomainData(data);
      // compute RMS (root-mean-square) of the waveform
      let sum = 0;
      for (let i = 0; i < data.length; i++) {
        const x = (data[i] - 128) / 128;
        sum += x * x;
      }
      const rms = Math.sqrt(sum / data.length);

      if (rms < SILENCE_THRESHOLD) {
        if (silenceStart === null) silenceStart = performance.now();
        else if (performance.now() - silenceStart > SILENCE_DELAY) {
          stopRecording(); // auto-stop on silence
          return;
        }
      } else {
        silenceStart = null; // voice detected → reset timer
      }

      requestAnimationFrame(detectSilence);
    }
    detectSilence();
  };

  async function stopRecording() {
    if (!recorderRef.current) return;
    setIsRecording(false);
    SpeechRecognition.stopListening();

    recorderRef.current.stopRecording(async () => {
      setVoiceState("loading"); // show spinner state
      streamRef.current.getTracks().forEach((t) => t.stop());

      const blankAudio = new Audio(
        "/blanks/Подготавливаю полное объяснение....wav"
      ); // Assuming blank.wav is in public folder
      try {
        await blankAudio.play();
        await new Promise((resolve) => (blankAudio.onended = resolve));
      } catch (error) {
        console.error("Error playing blank audio:", error);
      }

      const audio_data = recorderRef.current.getBlob();
      const blob = recorderRef.current.getBlob();
      const form = new FormData();
      form.append("file", blob, "question.wav");
      const q = new URLSearchParams({ session_id: sessionId });

      const res = await fetch(`/api/chat/voices?${q}`, {
        method: "POST",
        body: form,
      });
      if (!res.ok) {
        // alert("Ошибка сервера: " + res.status);
        setVoiceState("idle");
        return;
      }

      const aiBlob = await res.blob();
      const url = URL.createObjectURL(aiBlob);
      const audio = responseAudioRef.current;
      audio.src = url;
      audio.playbackRate = speed; // preserve whatever speed the user set
      audio.onended = () => {
        URL.revokeObjectURL(url);
        setVoiceState("idle");
      };

      await audio.play().catch(console.error);
      setVoiceState("playing");
    });
  }

  function stopPlayback() {
    const audio = responseAudioRef.current;
    audio.pause();
    setVoiceState("idle");
  }

  const [showChat, setShowChat] = useState(false);
  // const [isRecording, setRecording]  = useState(false)
  const [sessionId, setSessionId] = useState(null);

  useEffect(() => {
    if (sessionId === null && user_id) {
      (async () => {
        try {
          const resp = await fetch("/api/chat/sessions", {
            method: "POST",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              user_id: user_id,
              topic: selectedTopic, // TODO: change to selectedTopic
              task_number: null, // or whatever makes sense here
              is_task: false,
              course_name: selectedCourse, // TODO: change to selectedCourse
            }),
          });
          const data = await resp.json();
          setSessionId(data.session_id);
        } catch (err) {
          console.error("Failed to open chat session:", err);
        }
      })();
    }
  }, [showChat, sessionId, user_id, selectedTopic, selectedCourse]);

  useEffect(() => {
    if (sessionId && user_id) {
      // fetch history
      fetch(`/api/chat/sessions?session_id=${sessionId}`)
        .then(r => {
          if (!r.ok) throw new Error(r.statusText);
          return r.json();
        })
        .then(data => {
          // assume data.messages = [{ role: 'user'|'assistant', message: '…' }, …]
          const msgs = data.messages.map(m => ({
            sender: m.role === 'assistant' ? 'bot' : 'user',
            text:   m.message
          }));
          setConversation(msgs);
        })
        .catch(console.error);
    }
  }, [sessionId, user_id]);

  useEffect(() => {
    const aud = audioRef.current;
    if (!aud) return;
  
    const onError = () => {
      console.error('Audio error:', aud.error);
      setPlaying(false);
      alert(
        "Не удалось воспроизвести звук. " +
        "Попробуйте другой браузер или сообщите поддержке."
      );
    };
  
    aud.addEventListener('error', onError);
    return () => aud.removeEventListener('error', onError);
  }, []);
  
  

  const handleChatSend = async (payload, replyCallback) => {
    if (!sessionId) {
      return replyCallback("Не удалось открыть сессию чата, попробуйте позже.");
    }

    // build your body—text vs. voice:
    const body = {
      session_id: sessionId,
      message: typeof payload === "string" ? payload : undefined,
      user_code: "", // TODO: add user_code
      // if you're sending audio, you'd attach formData instead
    };

    let options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(body),
    };

    // If voice mode, swap to FormData:
    if (useVoice && payload instanceof Blob) {
      const form = new FormData();
      form.append("session_id", sessionId);
      form.append("audio", payload, "question.webm");
      options = { method: "POST", body: form };
    }

    try {
      console.log("Sending chat message:", options);
      const resp = await fetch("/api/chat/messages", options);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      // your old endpoint returned { bot_message: "..." }
      replyCallback(data.bot_message);
    } catch (err) {
      console.error("chat send error", err);
      replyCallback("Произошла ошибка, попробуйте снова.");
    }
  };

  const [speed, setSpeed] = useState(1);
  const SPEEDS = [0.75, 1, 1.25, 1.5, 2];

  function cycleSpeed() {
    const idx = SPEEDS.indexOf(speed);
    const next = SPEEDS[(idx + 1) % SPEEDS.length];
    setSpeed(next);
    if (audioRef.current) {
      audioRef.current.playbackRate = next;
    }
  }

  // Task modal state
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [taskData, setTaskData] = useState(null);
  const [taskLoading, setTaskLoading] = useState(false);
  const [taskError, setTaskError] = useState(null);
  

  const [slides, setSlides] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setCurrentIndex(-1);      // rewind audio/scroll
        setMaxVisibleIndex(-1);   // reset visible slides
        setSlides([]);            // clear out old slides
        setTheoryData(null);
    
        const protectedData = await withTokenRefresh(fetchProtectedData);
        setUserId(protectedData);
        if (!protectedData) {
          navigate("/login");
          return;
        }

        const resp = await fetch(
          `api/theories/by-topic/${selectedTopic}?user_id=${protectedData}&course_name=${selectedCourse}`,
          { method: "GET", headers: { Accept: "application/json" } }
        );
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

        const data = await resp.json();
        setTheoryData(data);
        setSlides(data.slides.slides); // Set the slides from the new structure

        const SUB_ENDPOINT = `/api/subscription/?user_id=${protectedData}`;
        const subscriptionResponse = await fetch(SUB_ENDPOINT, {
          method: "GET",
          headers: { Accept: "application/json" },
        });
        const subscriptionData = await subscriptionResponse.json();
        if (subscriptionData.status === "expired") {
          navigate("/courses");
        }
      } catch (err) {
        navigate("/login");
        return;
      }
    };
    fetchData();
  }, [selectedTopic, selectedCourse, navigate]);

  const fetchTask = async () => {
    setTaskLoading(true);
    setTaskError(null);
    try {
      // Replace with your actual API endpoint and params
      const resp = await fetch(
        `/api/tasks/unresolved?user_id=${user_id}&topic=Введение&course_name=Базовый курс по Python`,
        {
          method: "GET",
          headers: { Accept: "application/json" },
        }
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setTaskData(data);
    } catch (err) {
      setTaskError(err.message);
    } finally {
      setTaskLoading(false);
    }
  };

  // Toggle play / pause
  // inside TheoryPage component, replace your existing togglePlay with:

  const togglePlay = () => {
    const aud = audioRef.current;
    if (!aud) return;
  
    // If we're already playing, just pause
    if (playing) {
      aud.pause();
      clearTimeout(delayTimer.current);
      setPlaying(false);
      return;
    }
  
    // Determine which slide to play
    let nextIndex = currentIndex;
    const isMarkerSlide =
      currentIndex >= 0 &&
      slides[currentIndex].content.some(it =>
        /^!Task_in_Theory(?:_\d+)?!$/.test(it)
      );
  
    if (isMarkerSlide) {
      nextIndex = Math.min(currentIndex + 1, slides.length - 1);
    } else if (currentIndex < 0 || currentIndex >= slides.length) {
      nextIndex = 0;  // first time play
    }
  
    const slide = slides[nextIndex];
    if (!slide) return;
  
    // Fetch & play *inside* the click handler
    console.log("slide.audio_url", slide.audio_url);
    fetch(slide.audio_url)
      .then(res => res.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        aud.src = url;
        aud.load();
        // Safari will allow this .play() because we're still in the click
        return aud.play();
      })
      .then(() => {
        setCurrentIndex(nextIndex);
        setMaxVisibleIndex(Math.max(maxVisibleIndex, nextIndex));
        setPlaying(true);
        // clean up old objectURL later in your onended handler or useEffect
      })
      .catch(err => {
        console.error("Playback failed:", err);
        setPlaying(false);
      });
  };
  
  const [nextTopicLoading, setNextTopicLoading] = useState(false);
  const [nextTopicError, setNextTopicError] = useState(null);

  const fetchNextTopic = async () => {
    setNextTopicLoading(true);
    try {
      const resp = await fetch(
        `/api/theories/next/${selectedTopic}?user_id=${user_id}&course_name=${selectedCourse}`,
        { method: "GET", headers: { Accept: "application/json" } }
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const { topic: nextTopic } = await resp.json();
      navigate(`/theory_new?topic=${nextTopic}&course_name=${selectedCourse}`);
    } catch (e) {
      setNextTopicError(e.message);
    } finally {
      setNextTopicLoading(false);
    }
  };

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = speed;
    }
  }, [speed]);

  // Whenever we move to a new part, load & play it
  useEffect(() => {
    if (currentIndex < 0 || currentIndex >= slides.length) return;
    const aud = audioRef.current;
    const currentSlide = slides[currentIndex];

    // Reset audio state
    aud.pause();
    aud.currentTime = 0;

    aud.onloadedmetadata = null;

    // Load new audio
    fetch(currentSlide.audio_url)
      .then((response) => response.blob())
      .then((blob) => {
        const audioUrl = URL.createObjectURL(blob);
        aud.src = audioUrl;
        aud.playbackRate = speed;
        // Wait for audio to be loaded before playing
        aud.onloadedmetadata = () => {
          aud.playbackRate = speed;
          if (playing) {
            const p = aud.play();
            if (p) p.catch(() => setPlaying(false));
          }
        };

        aud.load();
      })
      .catch((error) => {
        console.error("Error loading audio:", error);
        setPlaying(false);
      });

    // auto-scroll the new part into view
    const el = partRefs.current[currentIndex];
    if (!el) return;
    el.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, [currentIndex, playing, slides]);

  useEffect(() => {
    // if we've hit a "!Task_in_Theory!" marker, pause playback
    if (
      currentIndex >= 0 &&
      slides[currentIndex].content.join("\n").trim() === "!Task_in_Theory!" &&
      audioRef.current
    ) {
      audioRef.current.pause();
      setPlaying(false);
    }
  }, [currentIndex]);

  // After each file ends, wait 0.5s then advance
  // After each file ends, but stop if slide has a task-marker
  // After each file ends, but stop if this slide has any Task_in_Theory marker
  useEffect(() => {
    const aud = audioRef.current;
    if (!aud) return;

    const onEnded = () => {
      const slide = slides[currentIndex];
      // detect either !Task_in_Theory! or numbered !Task_in_Theory_⟨n⟩! markers
      const hasMarker = slide.content.some((item) =>
        /^!Task_in_Theory(?:_\d+)?!$/.test(item)
      );

      if (hasMarker) {
        // stop here and don't advance
        setPlaying(false);
        return;
      }

      // otherwise schedule next slide after 0.5s
      delayTimer.current = setTimeout(() => {
        if (currentIndex + 1 < slides.length) {
          setCurrentIndex((ci) => ci + 1);
          setMaxVisibleIndex((mvi) => Math.max(mvi, currentIndex + 1));
        } else {
          setPlaying(false);
        }
      }, 500);
    };

    aud.addEventListener("ended", onEnded);
    return () => {
      aud.removeEventListener("ended", onEnded);
      clearTimeout(delayTimer.current);
    };
  }, [currentIndex, slides]);

  const taskMap = React.useMemo(() => {
    if (!theoryData?.tasks) return {};
    return theoryData.tasks.reduce((m, t) => {
      m[t.task_number] = t;
      return m;
    }, {});
  }, [theoryData]);

  if (!theoryData) return null;

  return (
    <div className={`theory-page ${isChatOpen ? "chat-open" : ""}`}>
      <div className="content">
        <h1 className="content__title">{theoryData.topic_front}</h1>

        <div className="summary-container" ref={summaryRef}>
          {slides.slice(0, maxVisibleIndex + 1).map((slide, idx) => {
            // Look for a marker like "!Task_in_Theory_2!"
            const markerItem = slide.content.find((item) =>
              /^!Task_in_Theory_(\d+)!$/.test(item)
            );
            const markerMatch =
              markerItem && markerItem.match(/^!Task_in_Theory_(\d+)!$/);
            const markerNumber = markerMatch ? Number(markerMatch[1]) : null;

            // Filter out the marker from the markdown content
            const markdownContent = slide.content
              .filter((item) => !/^!Task_in_Theory_\d+!$/.test(item))
              .join("\n");

            return (
              <React.Fragment key={idx}>
                <div
                  ref={(el) => (partRefs.current[idx] = el)}
                  className={`content__markdown-part ${idx === currentIndex ? 'active' : ''}`}
                  onClick={() => {
                    // Jump to this slide's audio position
                    setCurrentIndex(idx);
                    
                    // Show all slides from this point onwards (if clicking on earlier slide)
                    setMaxVisibleIndex(Math.max(maxVisibleIndex, idx));
                    
                    // If audio is currently playing, it will automatically load and play the new slide
                    // If not playing, just set the position so when user hits play it starts from here
                    if (!playing) {
                      setPlaying(true);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      code({ inline, className, children, ...props }) {
                        const match = /language-(\w+)/.exec(className || "");
                        if (!inline && match && match[1] === 'mermaid') {
                          return <Mermaid chart={String(children).trim()} />;
                        }
                        if (!inline && match) {
                          const lang = match[1];
                          const value = String(children).replace(/\n$/, "");
                          return (
                            <div className="markdown-code-block">
                              <div className="th-code-header">
                                <span className="lang-label">{lang}</span>
                                <button
                                  className="copy-btn"
                                  onClick={() => copyToClipboard(value)}
                                >
                                  Копировать код
                                </button>
                              </div>
                              <SyntaxHighlighter
                                language={lang}
                                style={irBlack}
                                wrapLongLines
                                customStyle={{ margin: 0, padding: "12px" }}
                              >
                                {value}
                              </SyntaxHighlighter>
                            </div>
                          );
                        }
                        return (
                          <code className={className} {...props}>
                            {children}
                          </code>
                        );
                      },
                    }}
                  >
                    {markdownContent}
                  </ReactMarkdown>
                </div>

                {taskMap[markerNumber] != null && (
                  <button
                    className="th-task-button"
                    onClick={() => {
                      const task = taskMap[markerNumber];
                      if (task) {
                        setTaskData(task);
                        setShowTaskModal(true);
                        audioRef.current?.pause();
                        setPlaying(false);
                      }
                    }}
                  >
                    задача
                  </button>
                )}
                {idx === slides.length - 1 && !playing && (
                  <button
                    className="th-next-topic-button"
                    onClick={fetchNextTopic}
                  >
                    следующая тема
                  </button>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <audio
        ref={audioRef}
        playsInline
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          opacity: 0,
          pointerEvents: 'none'
        }}
      />

      <div className="audio-footer">
        <div className="audio-footer-left">
          {/* Voice button: start or stop recording */}
          {voiceState === "idle" && (
            <button className="footer-btn voice-btn" onClick={startRecording}>
              <MicIcon className="icon" /> спросить
            </button>
          )}

          {voiceState === "recording" && (
            <button
              className="footer-btn voice-btn recording"
              onClick={stopRecording}
            >
              <MicIcon className="icon" /> говорите…
            </button>
          )}

          {voiceState === "loading" && (
            <button className="footer-btn voice-btn" disabled>
              {/* you can swap this for a spinner icon */}
              <MicIcon className="icon" /> загрузка…
            </button>
          )}

          {voiceState === "playing" && (
            <button className="footer-btn voice-btn" onClick={stopPlayback}>
              <MicIcon className="icon" /> остановить
            </button>
          )}

          {/* Chat button */}
          <button
            className="footer-btn chat-btn"
            onClick={() => {
              setIsRecording(false);
              setVoiceState("idle");
              // Clean up all voice-related state when opening chat
              if (isRecording) stopRecording();
              if (voiceState === "playing") stopPlayback();
              console.log(voiceState)

              const respAud = responseAudioRef.current;
              if (respAud) {
                     respAud.pause();
                     // clear out the URL so no “ended”/“play” events can re‐fire
                     respAud.src = "";
                   }

              const slideAud = audioRef.current;
              if (slideAud) {
                        slideAud.pause();
                        slideAud.src = "";
                    }
                   
              
              // Reset voice state to idle to prevent interference
              setVoiceState("idle");
              
              setIsChatOpen(o => !o);
            }}
            
          >
            <ChatIcon className="icon" />
            чат
          </button>

          {/* Inline chat modal */}
          {isChatOpen && (
            <ChatModal
            mode="text"
            onClose={() => setIsChatOpen(false)}
            onSend={(msg, cb) => {
              // when you send a new message, also append to conversation
              setConversation(prev => [...prev, { sender: 'user', text: msg }]);
              handleChatSend(msg, botReply => {
                setConversation(prev => [...prev, { sender: 'bot', text: botReply }]);
                cb(botReply);
              });
            }}
            initialMessages={conversation}
            asSidebar={true}
          />
        )}
        </div>

        <div className="controls-audio">
          <button
            className="control-audio-button"
            onClick={() =>
              audioRef.current &&
              (audioRef.current.currentTime = Math.max(
                0,
                audioRef.current.currentTime - 5
              ))
            }
          >
            <LeftIcon className="icon" />
          </button>
          <button
            className="control-audio-button play-button"
            onClick={togglePlay}
          >
            {playing ? <PauseIcon className="icon" /> : <PlayIcon className="icon" />}
          </button>
          <button
            className="control-audio-button"
            onClick={() =>
              audioRef.current &&
              (audioRef.current.currentTime = Math.min(
                audioRef.current.duration,
                audioRef.current.currentTime + 5
              ))
            }
          >
            <RightIcon className="icon" />
          </button>
          <button
            className="control-audio-button speed-button"
            onClick={cycleSpeed}
          >
            {speed}×
          </button>
        </div>
        <div className="audio-footer-right">
          <button className="summary-button" onClick={() => setShowModal(true)}>
            <BookIcon className="icon" />
            конспект
          </button>
          <Link
            to="/courses"
            className="th-home-button"
            title="К списку курсов"
          >
            <HomeIcon className="home-icon" />
          </Link>
        </div>
      </div>

      {showModal && (
        <div className="modal-th-overlay" onClick={() => setShowModal(false)}>
          <div
            className="modal-th-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="modal-th-close"
              onClick={() => setShowModal(false)}
            >
              ×
            </button>
            <Routes
              location={{
                pathname: pageLoc.pathname,
                search: `?topic=${encodeURIComponent(
                  selectedTopic
                )}&course_name=${encodeURIComponent(selectedCourse)}`,
              }}
            >
              <Route path="*" element={<Theory disableSidebar={true} disableTasks={true} />} />
            </Routes>
          </div>
        </div>
      )}

      {showTaskModal && (
        <TaskModal
          taskData={taskData}
          loading={taskLoading}
          initialCode={taskData?.code}
          onClose={() => setShowTaskModal(false)}
          /* ← add these two props: */
          courseName={selectedCourse}
          userId={user_id}
        />
      )}
    </div>
  );
}
